import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

type SiteHeaderProps = {
  compact?: boolean
}

export function SiteHeader({ compact = false }: SiteHeaderProps) {
  return (
    <header className="sticky top-0 z-50 bg-surface/80 backdrop-blur-md border-b border-outline-variant/30">
      <div
        className={`flex justify-between items-center px-lg w-full max-w-container-max mx-auto ${
          compact ? 'h-[48px]' : 'h-[72px]'
        }`}
      >
        <Link
          to="/"
          className={`font-display-lg tracking-tighter text-primary ${
            compact ? 'text-[24px]' : 'text-[32px]'
          }`}
        >
          HORATIX
        </Link>
        {compact ? null : (
          <div className="flex items-center gap-md">
            <Link
              to="/watch-builder"
              className="bg-primary text-on-primary px-md py-xs rounded-lg font-spec-label text-spec-label uppercase tracking-widest hover:bg-secondary transition-all active:scale-95"
            >
              Launch AI Studio
            </Link>
          </div>
        )}
      </div>
    </header>
  )
}

export function SiteFooter() {
  return (
    <footer className="bg-surface-dim border-t border-outline-variant/10">
      <div className="max-w-container-max mx-auto flex flex-col md:flex-row justify-between px-lg items-center py-md gap-md">
        <div className="font-display-lg text-[24px] text-primary">HORATIX</div>
        <div className="flex gap-lg flex-wrap justify-center">
          <a
            className="text-secondary font-spec-label text-spec-label hover:text-primary transition-colors"
            href="#"
          >
            Privacy Policy
          </a>
          <a
            className="text-secondary font-spec-label text-spec-label hover:text-primary transition-colors"
            href="#"
          >
            Technical Support
          </a>
          <a
            className="text-secondary font-spec-label text-spec-label hover:text-primary transition-colors"
            href="#"
          >
            Terms of Service
          </a>
        </div>
        <div className="text-secondary font-spec-label text-spec-label opacity-60">
          © {new Date().getFullYear()} Horatix Horology. All rights reserved.
        </div>
      </div>
    </footer>
  )
}

type SiteLayoutProps = {
  children: ReactNode
  /** When true (e.g., the builder route), main fills viewport and clips overflow. */
  fixedHeight?: boolean
  /** Slim header without CTA — used inside AI Studio. */
  compactHeader?: boolean
}

export function SiteLayout({
  children,
  fixedHeight = false,
  compactHeader = false,
}: SiteLayoutProps) {
  return (
    <div
      className={`site-shell bg-background text-on-background font-body-md flex flex-col${
        fixedHeight ? ' site-shell--fixed h-screen overflow-hidden' : ' min-h-screen'
      }`}
    >
      <SiteHeader compact={compactHeader} />
      <main
        className={`site-shell-main flex flex-col${
          fixedHeight ? ' flex-1 min-h-0 overflow-hidden' : ''
        }`}
      >
        {children}
      </main>
      {fixedHeight ? null : <SiteFooter />}
    </div>
  )
}
